import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { enrollmentsAPI } from '../api';
import './CourseEnrollmentsManager.css'; 

const CourseEnrollmentsManager = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingEnrollment, setEditingEnrollment] = useState(null);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      setLoading(true);
      const data = await enrollmentsAPI.getAll();
      setEnrollments(data.enrollments);
      setStats(data.stats);
    } catch (error) {
      toast.error('Failed to fetch enrollments');
      console.error('Error fetching enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEnrollment = async (enrollmentId, updates) => {
    try {
      await enrollmentsAPI.update(enrollmentId, updates);
      toast.success('Enrollment updated successfully');
      fetchEnrollments();
      setEditingEnrollment(null);
    } catch (error) {
      toast.error('Failed to update enrollment');
    }
  };

  // Rest of the component remains the same...

  const filteredEnrollments = enrollments.filter(enrollment => {
    const matchesSearch = 
      enrollment.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enrollment.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enrollment.course?.title?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || enrollment.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading enrollments...</p>
      </div>
    );
  }

  return (
    <div className="enrollments-manager">
      <div className="page-header">
        <h1>Course Enrollments</h1>
        <div className="stats-cards">
          <div className="stat-card">
            <h3>Total Enrollments</h3>
            <p className="stat-number">{stats.totalEnrollments || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Active</h3>
            <p className="stat-number">{stats.activeEnrollments || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Completed</h3>
            <p className="stat-number">{stats.completedEnrollments || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Unique Students</h3>
            <p className="stat-number">{stats.totalUsersEnrolled || 0}</p>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by student name, email, or course..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="status-filter">
          <label>Filter by status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="dropped">Dropped</option>
          </select>
        </div>
      </div>

      {stats.byCourse && Object.keys(stats.byCourse).length > 0 && (
        <div className="course-stats">
          <h3>Enrollments by Course</h3>
          <div className="course-stats-grid">
            {Object.entries(stats.byCourse).map(([courseName, count]) => (
              <div key={courseName} className="course-stat-card">
                <span className="course-name">{courseName}</span>
                <span className="enrollment-count">{count} students</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="enrollments-table-container">
        <table className="enrollments-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Course</th>
              <th>Enrolled Date</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEnrollments.map((enrollment) => (
              <tr key={enrollment._id}>
                <td className="user-cell">
                  <div className="user-info">
                    {enrollment.user?.picture ? (
                      <img src={enrollment.user.picture} alt={enrollment.user.name} className="user-avatar" />
                    ) : (
                      <div className="user-avatar-placeholder">
                        {enrollment.user?.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="user-name">{enrollment.user?.name || 'N/A'}</div>
                      <div className="user-email-small">{enrollment.user?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="course-cell">
                  <strong>{enrollment.course?.title || 'Unknown Course'}</strong>
                </td>
                <td>{new Date(enrollment.enrolledAt).toLocaleDateString()}</td>
                <td>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${enrollment.progress || 0}%` }}
                    />
                    <span className="progress-text">{enrollment.progress || 0}%</span>
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${enrollment.status}`}>
                    {enrollment.status}
                  </span>
                </td>
                <td>
                  <span className={`payment-badge ${enrollment.paymentStatus}`}>
                    {enrollment.paymentStatus}
                  </span>
                </td>
                <td className="actions-cell">
                  <button 
                    onClick={() => setEditingEnrollment(enrollment)} 
                    className="action-btn edit-btn"
                    title="Edit Enrollment"
                  >
                    ✏️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredEnrollments.length === 0 && (
          <div className="no-data">No enrollments found</div>
        )}
      </div>

      {/* Edit Enrollment Modal */}
      {editingEnrollment && (
        <div className="modal-overlay" onClick={() => setEditingEnrollment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Enrollment</h2>
              <button className="close-btn" onClick={() => setEditingEnrollment(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="edit-form">
                <div className="form-group">
                  <label>Student:</label>
                  <input 
                    type="text" 
                    value={editingEnrollment.user?.name || ''} 
                    disabled 
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Course:</label>
                  <input 
                    type="text" 
                    value={editingEnrollment.course?.title || ''} 
                    disabled 
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Status:</label>
                  <select 
                    value={editingEnrollment.status}
                    onChange={(e) => setEditingEnrollment({
                      ...editingEnrollment,
                      status: e.target.value
                    })}
                    className="form-select"
                  >
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="dropped">Dropped</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Progress (%):</label>
                  <input 
                    type="number" 
                    min="0" 
                    max="100"
                    value={editingEnrollment.progress}
                    onChange={(e) => setEditingEnrollment({
                      ...editingEnrollment,
                      progress: parseInt(e.target.value)
                    })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Payment Status:</label>
                  <select 
                    value={editingEnrollment.paymentStatus}
                    onChange={(e) => setEditingEnrollment({
                      ...editingEnrollment,
                      paymentStatus: e.target.value
                    })}
                    className="form-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="free">Free</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button 
                    onClick={() => handleUpdateEnrollment(editingEnrollment._id, {
                      status: editingEnrollment.status,
                      progress: editingEnrollment.progress,
                      paymentStatus: editingEnrollment.paymentStatus
                    })}
                    className="save-btn"
                  >
                    Save Changes
                  </button>
                  <button 
                    onClick={() => setEditingEnrollment(null)}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseEnrollmentsManager;